import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as authApi from '../../api/auth';
import { AuthShell } from './AuthShell';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input } from '../common/Input';
import { PasswordStrengthMeter } from '../common/PasswordStrengthMeter';
import { apiError } from '../../utils/apiError';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const mismatch = Boolean(confirmPassword) && password !== confirmPassword;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!token) {
      setError('This reset link is missing its token. Request a new one.');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({ token, password });
      setSuccess(true);
    } catch (err) {
      setError(apiError(err, 'Password reset failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell subtitle="Set your new password">
      {success ? (
        <div className="text-center space-y-4">
          <svg className="w-12 h-12 mx-auto text-status-up" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-700 dark:text-gray-300">
            Password changed. All other sessions have been signed out.
          </p>
          <Link to="/login" className="block text-brand-600 dark:text-brand-400 hover:underline">
            Sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <Alert tone="error">{error}</Alert>}

          <div>
            <Field label="New Password" required>
              {p => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              )}
            </Field>
            <PasswordStrengthMeter password={password} />
          </div>

          <Field
            label="Confirm New Password"
            required
            error={mismatch ? 'Passwords do not match' : undefined}
          >
            {p => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
            )}
          </Field>

          <Button type="submit" loading={loading} className="w-full" size="lg">
            {loading ? 'Resetting…' : 'Reset password'}
          </Button>

          <p className="text-center text-sm">
            <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline">Back to sign in</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
